/**
 * Typed client for /api/loader. Response keys and query parameters match the
 * Worker routes (ported verbatim from the previous Pages Functions).
 */
import { deleteJson, getJson, postJson, putJson } from '@/lib/api';
import { LOADER_API_BASE as BASE } from '../constants';
import type {
  Antenna,
  FeatureToggle,
  Mission,
  Network,
  PriorityTier,
  SavedScenario,
  TdrsSatellite,
} from '../types';

const net = (network: string) => `network=${encodeURIComponent(network)}`;

export interface SyncResponse {
  changes: {
    table_name: string;
    record_id: string;
    network_id: string | null;
    action: string;
    timestamp: string;
  }[];
  serverTime: string;
  hasChanges: boolean;
}

export interface ElevationResponse {
  results: { lat: number; lon: number; elevation_m: number | null }[];
  source: string;
}

export class FeedDegradedError extends Error {
  source: string;
  reason: string;
  constructor(source: string, reason: string) {
    super(`Feed ${source} degraded: ${reason}`);
    this.source = source;
    this.reason = reason;
  }
}

export const loaderApi = {
  networks: () => getJson<{ networks: Network[] }>(`${BASE}/networks`).then((r) => r.networks),
  antennas: (network: string) =>
    getJson<{ antennas: Antenna[] }>(`${BASE}/antennas?${net(network)}`).then((r) => r.antennas),
  missions: (network: string) =>
    getJson<{ missions: Mission[] }>(`${BASE}/missions?${net(network)}`).then((r) => r.missions),
  priorityTiers: (network: string) =>
    getJson<{ tiers: PriorityTier[] }>(`${BASE}/priority-tiers?${net(network)}`).then((r) => r.tiers),
  featureToggles: () =>
    getJson<{ toggles: FeatureToggle[] }>(`${BASE}/feature-toggles`).then((r) => r.toggles),
  tdrss: (network: string) =>
    getJson<{ tdrss: TdrsSatellite[] }>(`${BASE}/tdrss?${net(network)}`).then((r) => r.tdrss),
  scenarios: (network: string) =>
    getJson<{ scenarios: SavedScenario[] }>(`${BASE}/scenarios?${net(network)}`).then((r) => r.scenarios),
  sync: (sinceIso: string, network: string) =>
    getJson<SyncResponse>(`${BASE}/sync?since=${encodeURIComponent(sinceIso)}&${net(network)}`),

  setFeatureToggle: (id: string, enabled: boolean) =>
    putJson<{ success: boolean }>(`${BASE}/feature-toggles`, { id, enabled }),

  createAntenna: (networkId: string, antenna: Antenna) =>
    postJson<{ success: boolean; id: string }>(`${BASE}/antennas`, { antenna, networkId }),
  updateAntenna: (networkId: string, antenna: Antenna) =>
    putJson<{ success: boolean }>(`${BASE}/antennas/${encodeURIComponent(antenna.id)}?${net(networkId)}`, {
      antenna,
    }),
  deleteAntenna: (networkId: string, id: string) =>
    deleteJson<{ success: boolean }>(`${BASE}/antennas/${encodeURIComponent(id)}?${net(networkId)}`),

  createMission: (networkId: string, mission: Mission) =>
    postJson<{ success: boolean; id: string }>(`${BASE}/missions`, { mission, networkId }),
  updateMission: (networkId: string, mission: Mission) =>
    putJson<{ success: boolean }>(`${BASE}/missions/${encodeURIComponent(mission.id)}?${net(networkId)}`, {
      mission,
    }),
  deleteMission: (networkId: string, id: string) =>
    deleteJson<{ success: boolean }>(`${BASE}/missions/${encodeURIComponent(id)}?${net(networkId)}`),

  createTier: (networkId: string, tier: PriorityTier) =>
    postJson<{ success: boolean }>(`${BASE}/priority-tiers`, { tier, networkId }),
  replaceTiers: (networkId: string, tiers: PriorityTier[]) =>
    putJson<{ success: boolean }>(`${BASE}/priority-tiers`, { tiers, networkId }),

  saveScenario: (networkId: string, scenario: SavedScenario) =>
    postJson<{ success: boolean; id: string }>(`${BASE}/scenarios`, { scenario, networkId }),
  updateScenario: (networkId: string, scenario: SavedScenario) =>
    putJson<{ success: boolean }>(`${BASE}/scenarios/${encodeURIComponent(scenario.id)}?${net(networkId)}`, {
      scenario,
    }),
  deleteScenario: (networkId: string, id: string) =>
    deleteJson<{ success: boolean }>(`${BASE}/scenarios/${encodeURIComponent(id)}?${net(networkId)}`),

  createTdrs: (networkId: string, satellite: TdrsSatellite) =>
    postJson<{ success: boolean; id: string }>(`${BASE}/tdrss`, { satellite, networkId }),
  updateTdrs: (networkId: string, satellite: TdrsSatellite) =>
    putJson<{ success: boolean }>(`${BASE}/tdrss/${encodeURIComponent(satellite.id)}?${net(networkId)}`, {
      satellite,
    }),
  deleteTdrs: (networkId: string, id: string) =>
    deleteJson<{ success: boolean }>(`${BASE}/tdrss/${encodeURIComponent(id)}?${net(networkId)}`),

  seed: (network: string) => postJson<{ success: boolean }>(`${BASE}/seed?${net(network)}`, {}),

  /** CelesTrak proxy URL for a group (TLE text). */
  celestrakUrl: (
    group: string,
    opts: { supplemental?: boolean; format?: 'tle' | 'json' | 'csv' | '2le' } = {},
  ) =>
    `${BASE}/celestrak?group=${encodeURIComponent(group)}&format=${opts.format ?? 'tle'}${opts.supplemental ? '&supplemental=1' : ''}`,

  /** Live feed through the Worker proxy. A degraded response (served from the Worker's stale cache or empty) is an error so callers can use the bundled snapshot. */
  feed: async <T>(source: string, path?: string, signal?: AbortSignal): Promise<T> => {
    const url = `${BASE}/feed-proxy?source=${encodeURIComponent(source)}${path ? `&path=${encodeURIComponent(path)}` : ''}`;
    const res = await fetch(url, { credentials: 'same-origin', signal });
    const degraded = res.headers.get('X-Loader-Feed-Degraded');
    if (degraded) throw new FeedDegradedError(source, degraded);
    if (!res.ok) throw new Error(`Feed ${source} failed (${res.status})`);
    return (await res.json()) as T;
  },

  horizons: async (command: string, startIso: string, stopIso: string, step: string): Promise<string> => {
    const url = `${BASE}/horizons-proxy?command=${encodeURIComponent(command)}&start=${encodeURIComponent(startIso)}&stop=${encodeURIComponent(stopIso)}&step=${encodeURIComponent(step)}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Horizons ${command} failed (${res.status})`);
    return res.text();
  },

  elevation: (points: { lat: number; lon: number }[]) =>
    getJson<ElevationResponse>(
      `${BASE}/elevation?points=${encodeURIComponent(points.map((p) => `${p.lat},${p.lon}`).join('|'))}`,
    ),

  arowOem: async (): Promise<string> => {
    const res = await fetch(`${BASE}/arow-oem`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`AROW OEM failed (${res.status})`);
    return res.text();
  },

  dsnNow: <T>() => getJson<T>(`${BASE}/dsn-now`),
  spaceWeather: <T>() => getJson<T>(`${BASE}/space-weather`),
  artemisBlog: <T>() => getJson<T>(`${BASE}/artemis-blog`),
  spaceTrackCdm: <T>() => getJson<T>(`${BASE}/space-track-cdm`),
};
